import _ from 'lodash';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import cardValidator from 'card-validator';
import { CardData, useAcceptJs } from 'react-acceptjs';
import PaymentForm, { IPaymentFormData } from '@/shared/components/payment-form';
import { useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import { selectPaymentInfoPopupShown } from '@/store/slices/auth/selectors';
import { markPaymentInfoPopupShown } from '@/store/slices/auth';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { selectOnboardingUser, selectCreditCards } from '@/store/slices/onboarding/selectors';
import { AuthCardCreateRequest } from '@/shared/endpoints/customers';
import { CustomersController } from '@/shared/endpoints/customers';
import { ROUTES } from '@/shared/constants/routes';
import { CustomersStepTypeEnum, CustomersStepTypeEnumPaths } from '@/shared/endpoints/customers/models/enums';
import { stepPayloadHandler } from '../../helpers';
import { setPageType } from '@/shared/helpers/page-type';
import { useHeaderHeight } from '@/shared/components/header-height-context';

// Component
import { Box, Button, Modal } from '@/shared/ui';
import { SplitLayout } from '@/modules/onboarding/components/split-layout';
import { ConfirmModal } from '@/shared/components/confirm-modal';
import { CircularProgress } from '@mui/material';

import PaymentInfoImg from '@/assets/images/onboarding/payment_info.png';
import CardList from './components/cardList';
import CheckedIcon from '@/assets/icons/checked-circle-green.svg';
import HippacIcon from '@/assets/images/onboarding/hipaa.png';
import EncryptionIcon from '@/assets/images/onboarding/encryption.png';
import LockIcon from '@/assets/icons/lock.svg';

export interface Response {
    opaqueData: {
        dataDescriptor: string;
        dataValue: string;
    };
    messages: {
        resultCode: string;
        message: {
            code: string;
            text: string;
        }[];
    };
}

const Payment = () => {
    const formFocusedRef = useRef(false);
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const headerHeight = useHeaderHeight();
    const currentUser = useAppSelector(selectOnboardingUser)!;
    const paymentInfoPopupShown = !!useAppSelector(selectPaymentInfoPopupShown);
    const existingCreditCards = useAppSelector(selectCreditCards);
    const [deletingCardId, setDeletingCardId] = useState<string | number | null>(null);
    const [deletionLoading, setDeletionLoading] = useState(false);
    const [addPaymentLoading, setAddPaymentLoading] = useState(false);
    const [proceedWithoutPaymentWarningOpen, setProceedWithoutPaymentWarningOpen] = useState(false);
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const { dispatchData } = useAcceptJs({
        authData: {
            apiLoginID: import.meta.env.VITE_APP_AUTHORIZE_NET_LOGIN_ID,
            clientKey: import.meta.env.VITE_APP_AUTHORIZE_NET_CLIENT_KEY
        },
        environment: import.meta.env.VITE_APP_IS_PRODUCTION === 'true' ? 'PRODUCTION' : 'SANDBOX'
    });

    const _conv_page_type = existingCreditCards.length ? 'payments_credit_cards' : 'payment_form';
    useEffect(() => {
        setPageType(_conv_page_type);
    }, [_conv_page_type]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (paymentInfoPopupShown || existingCreditCards.length) return;
            if (!paymentInfoPopupShown && !formFocusedRef.current) {
                dispatch(markPaymentInfoPopupShown());
                setIsPromptOpen(true);
            }
        }, 5000);

        return () => clearTimeout(timer);
    }, [dispatch, paymentInfoPopupShown, existingCreditCards.length]);

    const onFocus = useCallback(() => {
        formFocusedRef.current = true;
    }, []);

    const openPaymentWarningModal = useCallback(() => {
        setProceedWithoutPaymentWarningOpen(true);
    }, []);

    const closePaymentWarningModal = useCallback(() => {
        setProceedWithoutPaymentWarningOpen(false);
    }, []);

    const proceedToReview = useCallback(async () => {
        if (!currentUser.userOnboarding?.details) return;
        const payload = _.cloneDeep(currentUser.userOnboarding);
        payload.details = {
            ...payload.details!,
            ...stepPayloadHandler(currentUser?.userOnboarding?.details?.currentStep, CustomersStepTypeEnum.Review)
        };
        await dispatch(CustomersController.updateMetadata(payload));
        navigate(ROUTES.ONBOARDING.MAIN.replace(':stepType?', CustomersStepTypeEnumPaths.REVIEW));
    }, [dispatch, currentUser.userOnboarding, navigate]);

    const handleDelete = useCallback(
        async (id: string | number) => {
            try {
                if (!currentUser.userOnboarding?.details) return;
                setDeletionLoading(true);
                const deletingCard = existingCreditCards.find(card => card.id === id);

                if (!deletingCard) return;

                const deletionRes = await dispatch(CustomersController.paymentMethodDelete(deletingCard));
                if (!deletionRes?.type?.includes('fulfilled')) {
                    throw new Error('Error deleting payment method');
                }
                const payload = _.cloneDeep(currentUser.userOnboarding);
                payload.details = {
                    ...payload.details!,
                    currentStep: CustomersStepTypeEnum.Payment
                };
                await dispatch(CustomersController.updateMetadata(payload));
            } catch (error) {
                toast((error as Error).message, { autoClose: 3000, type: 'error' });
            } finally {
                setDeletionLoading(false);
                setDeletingCardId(null);
            }
        },
        [currentUser.userOnboarding, dispatch, existingCreditCards]
    );

    const handleSave = useCallback(
        async (formData: IPaymentFormData) => {
            try {
                const { addressLine1, addressLine2, unit, city, state, zip } = currentUser.addresses[0];
                if (formData.hasDifferentBillingAddress === 1) {
                    formData.billingDetails = {
                        ...formData.billingDetails,
                        addressLine1: addressLine1!,
                        addressLine2: addressLine2!,
                        unit,
                        city,
                        state,
                        zip
                    };
                }
                setAddPaymentLoading(true);
                const [month, year] = formData.expirationDate.split('/');
                const fullName = `${formData?.firstName} ${formData?.lastName}`;
                const cardData: CardData = {
                    cardNumber: formData.cardNumber.toString().replaceAll(' ', ''),
                    month,
                    year,
                    cardCode: formData.cvv.toString(),
                    fullName,
                    zip: formData?.billingDetails?.zip || ''
                };
                const response: Response = await dispatchData({ cardData });
                const brand = cardValidator.number(formData.cardNumber)?.card?.type;
                const payload: AuthCardCreateRequest = {
                    cardId: response.opaqueData?.dataValue,
                    name: fullName,
                    lastFourDigit: formData.cardNumber.slice(-4),
                    expiryMonth: formData.expirationDate.split('/')[0],
                    expiryYear: formData.expirationDate.split('/')[1],
                    brand,
                    preferred: true,
                    accountId: currentUser?.id,
                    billingDetails: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        ...formData.billingDetails
                    }
                };

                const result = await dispatch(CustomersController.addPaymentMethod(payload));
                if (!result?.type?.includes('fulfilled')) {
                    throw new Error('Error adding payment method');
                }
            } catch (error) {
                toast('Failed to save payment method', { autoClose: 3000, type: 'error' });
            } finally {
                setAddPaymentLoading(false);
            }
        },
        [currentUser?.id, dispatch, dispatchData, currentUser.addresses]
    );

    const goBackToMedbox = useCallback(() => {
        navigate(ROUTES.ONBOARDING.MAIN.replace(':stepType?', CustomersStepTypeEnumPaths.PRESCRIPTIONS));
    }, [navigate]);

    const paymentAssertionsUI = () => {
        return (
            <div className="mb-[30px] box-border flex flex-col gap-2.5 rounded-[15px] rounded-tr-[0px] bg-[#CCE3DA] p-[30px] pb-4.5 pt-3.5 md:pb-8 md:pt-4">
                <ul className="flex flex-col gap-5 md:gap-6">
                    <li className="flex items-center gap-3">
                        <img className="w-[27px]" src={CheckedIcon} alt="" />
                        <span className="font-atkinsonHyperlegible text-lg xl:text-2xl">No Charge Until Order Ships</span>
                    </li>
                    <li className="flex items-center gap-3">
                        <img className="w-[27px]" src={CheckedIcon} alt="" />
                        <span className="font-atkinsonHyperlegible text-lg xl:text-2xl">Free Shipping in Discreet Packaging </span>
                    </li>
                    <li className="flex items-start gap-3">
                        <img className="mt-1 w-[27px] 2xl:mt-0" src={CheckedIcon} alt="" />
                        <span className="font-atkinsonHyperlegible text-lg xl:text-2xl">
                            Your Copays Will Stay The Same{' '}
                            <p className="pt-3 text-sm sm:text-base md:text-xl 2xl:text-2xl">
                                (Copays are set by your insurance & should stay the same at any pharmacy you choose)
                            </p>
                        </span>
                    </li>
                    <li className="flex items-center gap-3">
                        <img className="w-[27px]" src={CheckedIcon} alt="" />
                        <span className="font-atkinsonHyperlegible text-lg xl:text-2xl">No Copays, No Charges</span>
                    </li>
                </ul>
            </div>
        );
    };

    return (
        <div onFocus={onFocus} className="pt-[25px]">
            <SplitLayout
                sideContent={
                    <div className="flex max-w-[530px] flex-col xl:max-w-[550px] 2xl:w-[635px] lg-max:flex-auto xl-max:w-full xl-max:items-center">
                        <h2 className="max-w-[383px] font-agrandir text-3xl md:text-center lg:text-left xl:text-4xl 2xl:text-5xl 2xl:leading-[60px] xl-max:text-center">
                            Pay your Copays with Ease
                        </h2>
                        <div className="box-border flex max-w-full flex-col gap-2.5 pb-4.5 pt-3.5 md:pb-8 md:pt-4 2xl:pb-14">
                            <p className="font-atkinsonHyperlegible text-base md:text-center md:text-xl lg:text-left 2xl:text-2xl">
                                Add a payment method to cover prescription copays and easily manage over-the-counter purchases. Rest
                                assured, your card will only be charged when your complete MedBox order — containing prescriptions, selected
                                vitamins, and over-the-counter medications — is shipped.
                            </p>
                        </div>
                        <div className="hidden xl:block">{paymentAssertionsUI()}</div>
                    </div>
                }
                mainContent={
                    <>
                        <div className="box-wrapper box-border flex w-full flex-1 flex-col items-center justify-start bg-white lg:pb-[77px]">
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%'
                                }}
                                bgWhite={true}
                                padding="0"
                                paddingLg="0"
                            >
                                {deletionLoading && (
                                    <div className="flex items-center justify-center">
                                        <CircularProgress sx={{ color: 'primary', width: '60px !important', height: '60px !important' }} />
                                    </div>
                                )}

                                {!!existingCreditCards.length && !deletionLoading && (
                                    <div className="flex flex-col">
                                        <CardList list={existingCreditCards} onDelete={setDeletingCardId} />

                                        <Button testId="button-payments-proceed-to-review" className="payment__proceed_to_review_btn" type="submit" onClick={proceedToReview}>
                                            Proceed to Review
                                        </Button>

                                        <Button
                                            testId="button-payments-back"
                                            className="payment__back_btn"
                                            onClick={goBackToMedbox}
                                            sx={{
                                                width: '100%',
                                                mt: '16px',
                                                [theme.breakpoints.down('md')]: {
                                                    mt: '10px'
                                                },
                                                fontFamily: 'DM Sans medium'
                                            }}
                                            theme="main"
                                            variant="outlined"
                                            disabled={addPaymentLoading}
                                        >
                                            Back
                                        </Button>
                                        <ConfirmModal
                                            open={!!deletingCardId}
                                            title="Are you sure you want to remove your card details?"
                                            confirmText="Yes, Remove"
                                            cancelText="Cancel Removal"
                                            onConfirm={() => handleDelete(deletingCardId!)}
                                            onCancel={() => setDeletingCardId(null)}
                                        />
                                    </div>
                                )}

                                {!existingCreditCards.length && !deletionLoading && (
                                    <div>
                                        <div className="flex items-center gap-2 pb-3.5 font-agrandir text-2xl md:pb-6 lg:pb-8 md-max:mx-auto md-max:max-w-[197px] md-max:flex-col lg-max:!text-2xl xl-max:text-xl">
                                            <img src={LockIcon} alt="" />
                                            <h2>SSL Encrypted & HIPAA Compliant</h2>
                                        </div>
                                        <PaymentForm
                                            isPreferred
                                            userAddress={currentUser?.addresses[0].fullAddress || ''}
                                            addPaymentLoading={addPaymentLoading}
                                            onSave={handleSave}
                                            cancelAddPaymentHandler={openPaymentWarningModal}
                                            headerOffset={headerHeight}
                                            withinModal={false}
                                        />
                                        <div className="flex flex-wrap items-center justify-center gap-3 pt-5 md:pt-8 lg:hidden lg:gap-4">
                                            <img src={HippacIcon} alt="" className="h-10 max-w-36 sm:h-auto" />
                                            <img src={EncryptionIcon} alt="" className="h-10 max-w-36 sm:h-auto" />
                                        </div>

                                        <ConfirmModal
                                            open={proceedWithoutPaymentWarningOpen}
                                            title="Add Payment Later"
                                            confirmText="I Understand"
                                            cancelText="Add Payment Now"
                                            cancelTheme="mintNoHoverReaction"
                                            cancelVariant="outlined"
                                            showEndIcon={false}
                                            onConfirm={proceedToReview}
                                            onCancel={closePaymentWarningModal}
                                        >
                                            <div className="text-center">
                                                <div>
                                                    I understand that by choosing to <b>Add Payment Later</b>, I must add a payment method
                                                    upon receiving my first invoice.
                                                </div>
                                                <div className="mt-4">
                                                    I will look out for my first invoice so there are no delays in receiving my MedBox.
                                                </div>
                                            </div>
                                        </ConfirmModal>
                                    </div>
                                )}
                            </Box>
                        </div>
                        <div className="mt-3 block xl:hidden">{paymentAssertionsUI()}</div>
                    </>
                }
            />
            <Modal
                open={isPromptOpen}
                onClose={() => setIsPromptOpen(false)}
                contentClassName="sm:max-w-[511px] max-w-[350px] md:!py-[50px] py-[30px] px-[20px]"
                defaultContentSizes={false}
                dataTestId="copay-questions"
            >
                <div data-testid="copay-questions-container" className="items-centers flex flex-col gap-5">
                    <img src={PaymentInfoImg} alt="" className="mx-auto" />
                    <h3 className="mx-auto max-w-[313px] text-center font-agrandir text-3xl" data-testid="copay-questions-title">Have questions about your copays?</h3>
                    <div className="flex flex-col gap-7 px-[7px] sm:px-0">
                        <p className="text-center font-atkinsonHyperlegible text-[17px] sm:text-xl" data-testid="copay-questions-content-text">
                            All copays are set by your insurance plan and stay the same across pharmacies. With MedBox, you only pay your
                            regular copays. Feel free to check with your insurance at any time to verify your copay amount.
                        </p>

                        <p className="text-center font-atkinsonHyperlegible text-[17px] sm:text-xl" data-testid="copay-questions-card-details-text">
                            <span className="pb-3 text-center font-atkinsonBold">Why We Need Your Card Details</span>
                            <br />
                            By keeping a payment card on file, we can efficiently handle any copay fees associated with your prescription
                            medications, as well as cover costs for any over-the-counter medications or vitamins you may need. This process
                            allows you to receive your medications without any delay.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2.5 pt-3 sm:gap-4">
                        <Button testId="button-payments-no-copays" className="payment__not_have_copay_btn" showEndIcon onClick={proceedToReview}>
                            I don’t have any copays
                        </Button>
                        <Button testId="button-payments-continue" className="payment__continue_btn" variant="outlined" onClick={() => setIsPromptOpen(false)}>
                            Continue to Payment
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Payment;

// export default withScrollRestoration(Payment);
